import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
} from '@mui/material'
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { useRef, useState } from 'react'
import { CalendarIcon } from '@/assets/icons/index'
import {
  completionDatePickerCancelSx,
  completionDatePickerSaveSx,
} from '@/constants/dashboardConstant'

// ==================== CompletionDatePicker ====================
// Editable completion-date cell. Selection-only (no typing), past dates
// disabled. Selecting a day does NOT close the popover — it closes only on
// Cancel, Save, or clicking outside. The input value reflects only the
// COMMITTED date and updates solely on a successful Save (via `onSave`).
const CompletionDatePicker = ({ value, onSave }) => {
  // ==================== State ====================
  const [open, setOpen] = useState(false)
  // Committed value shown in the input — only updated on Save (API success).
  const [committedDate, setCommittedDate] = useState(
    value ? dayjs(value, 'DD/MM/YYYY') : null
  )
  // Draft value the calendar edits while open; discarded unless saved.
  const [draftDate, setDraftDate] = useState(committedDate)
  // True while the save request is in flight.
  const [saving, setSaving] = useState(false)
  // Anchor for the calendar popover (the trigger text field).
  const anchorRef = useRef(null)

  // ==================== Derived Data ====================
  // Save is only allowed once a date has been picked and no save is running.
  const isSaveDisabled = !draftDate || saving

  // ==================== Handlers ====================
  const handleOpen = () => {
    // Start each editing session from the committed value.
    setDraftDate(committedDate)
    setOpen(true)
  }

  const handleClose = () => {
    // Fired on outside-click (and programmatic close). Discard the draft so the
    // committed value/input is unchanged.
    setDraftDate(committedDate)
    setOpen(false)
  }

  const handleCancel = () => {
    // Cancel discards the draft and closes without committing.
    setDraftDate(committedDate)
    setOpen(false)
  }

  const handleSave = async () => {
    // Guard against saving an empty value or a concurrent save.
    if (isSaveDisabled) return
    setSaving(true)
    try {
      // Persist via the API (YYYY-MM-DD). Only commit locally on success.
      const succeeded = await onSave?.(draftDate.format('YYYY-MM-DD'))
      if (succeeded) {
        setCommittedDate(draftDate)
        setOpen(false)
      }
    } finally {
      setSaving(false)
    }
  }

  // ==================== Return ====================
  return (
    <>
      {/* Read-only trigger showing only the committed value. Clicking it opens
          the calendar; typing is blocked. */}
      <TextField
        size="small"
        sx={{ width: 170 }}
        placeholder="DD/MM/YYYY"
        value={committedDate ? committedDate.format('DD/MM/YYYY') : ''}
        inputRef={anchorRef}
        onClick={handleOpen}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleOpen} edge="end">
                <CalendarIcon
                  sx={{ color: 'var(--forbrand-primary)', fontSize: 20 }}
                />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateCalendar
            value={draftDate}
            onChange={newValue => setDraftDate(newValue)}
            disablePast
          />
        </LocalizationProvider>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            p: 1.5,
            pt: 0,
          }}
        >
          <Button
            onClick={handleCancel}
            variant="text"
            sx={completionDatePickerCancelSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isSaveDisabled}
            sx={completionDatePickerSaveSx}
          >
            Save
          </Button>
        </Box>
      </Popover>
    </>
  )
}

export default CompletionDatePicker
